import type { ArtifactListItem } from '@agentisai/shared-types';

const TYPE_LABELS: Record<string, string> = {
  'text/markdown': 'Markdown',
  'text/plain': 'Text',
  'text/html': 'HTML',
  'text/csv': 'CSV',
  'application/pdf': 'PDF',
  'application/json': 'JSON',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
};

/** Friendly type label — a known map, else the uppercased subtype (`svg+xml` →
 * `SVG+XML`). Any `;charset=…` parameter is stripped first, so a known type that
 * arrives parameterised (`text/markdown; charset=utf-8`) still maps. */
export function formatArtifactType(contentType: string): string {
  const bare = (contentType.split(';')[0] ?? contentType).trim().toLowerCase();
  const known = TYPE_LABELS[bare];
  if (known) return known;
  const subtype = bare.split('/')[1] ?? bare;
  return subtype.toUpperCase();
}

/** Human byte size — `B` / `KB` (integer) / `MB` (one decimal). The MB rollover is
 * keyed on the *rounded* KB so a size just under 1 MiB reads `1.0 MB`, not `1024 KB`. */
export function formatArtifactSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = Math.round(bytes / 1024);
  if (kb < 1024) return `${kb} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Short date — `19 Jul` (en-GB day + short month). Formatted in UTC (matching the
 * artifact's native `lastModified`), which also keeps it deterministic regardless
 * of the viewer's / test runner's timezone. Unparseable dates → `''`.
 */
export function formatArtifactDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * The FileCard meta line — `type · size · date` — composed from an artifact's
 * fields (frontend-architecture R2: derived at render, never stored). Any empty
 * segment (e.g. an unparseable date) is dropped so the separators stay clean.
 */
export function formatArtifactMeta(
  item: Pick<ArtifactListItem, 'contentType' | 'sizeBytes' | 'lastModified'>,
): string {
  return [
    formatArtifactType(item.contentType),
    formatArtifactSize(item.sizeBytes),
    formatArtifactDate(item.lastModified),
  ]
    .filter(Boolean)
    .join(' · ');
}
